import React, { useMemo } from 'react';
import { AlertTriangle, Copy, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GenericTag, type StatusType } from '@/styles/GenericTag';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { StandardButton } from '@/styles/StandardButtons';
import type { AiBidComparison, AiLineItem, AiOffer, AiVendor, OfferStatus, TotalBasis } from '../types';

const money = (value: number | null) => (value === null ? null : `$${value.toLocaleString('en-US', { minimumFractionDigits: value % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);

const OFFER_TAGS: Record<OfferStatus, { tag: StatusType; label: string }> = {
  included: { tag: 'approved', label: 'Included' },
  excluded: { tag: 'rejected', label: 'Excluded' },
  not_mentioned: { tag: 'draft', label: 'Not mentioned' },
};

const BASIS_LABEL: Record<TotalBasis, string> = {
  stated: 'Stated in the bid',
  recorded: 'Recorded in KrewUp',
  sum_of_items: 'Sum of priced items',
  not_found: 'No total found',
};

const CONFIDENCE_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' } as const;
const CONFIDENCE_TAG: Record<AiBidComparison['recommendation']['confidence'], StatusType> = { high: 'approved', medium: 'pending', low: 'rejected' };

/** What one vendor offers for one line item: status, quantity x unit price and total. */
function OfferCell({ offer }: { offer: AiOffer }) {
  const { tag, label } = OFFER_TAGS[offer.status];
  const quantity = offer.quantity !== null ? `${offer.quantity.toLocaleString('en-US')}${offer.unit ? ` ${offer.unit}` : ''}` : null;
  const unitPrice = money(offer.unitPrice);
  const total = money(offer.totalPrice);
  return (
    <div className="space-y-1 py-1 whitespace-normal break-words">
      <GenericTag type="status" value={tag}>{label}</GenericTag>
      {offer.status === 'included' && (quantity || unitPrice || total) && (
        <div className="text-xs text-gray-600">
          {[quantity, unitPrice && `@ ${unitPrice}`].filter(Boolean).join(' ')}
          {total && <div className="text-sm font-medium text-gray-900">{total}</div>}
          {!total && <div className="text-gray-400">No price</div>}
        </div>
      )}
      {offer.note && <div className="text-xs text-gray-500">{offer.note}</div>}
    </div>
  );
}

function VendorTotals({ vendors }: { vendors: AiVendor[] }) {
  const lowest = useMemo(() => {
    const priced = vendors.filter((v) => v.total !== null).map((v) => v.total as number);
    return priced.length > 1 ? Math.min(...priced) : null;
  }, [vendors]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {vendors.map((vendor) => (
        <div key={vendor.name} className="rounded-lg border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium truncate">{vendor.name}</span>
            {lowest !== null && vendor.total === lowest && <GenericTag type="status" value="awarded">Lowest</GenericTag>}
          </div>
          <div className="text-xl font-semibold mt-1">{vendor.total !== null ? money(vendor.total) : <span className="text-gray-400">No total</span>}</div>
          <div className="text-xs text-gray-500">{BASIS_LABEL[vendor.totalBasis]}</div>
        </div>
      ))}
    </div>
  );
}

function ClarificationEmail({ email }: { email: AiBidComparison['clarificationEmails'][number] }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Subject: ${email.subject}\n\n${email.body}`);
    } catch {
      window.prompt('Copy the email:', `Subject: ${email.subject}\n\n${email.body}`);
    }
  };
  const mailto = `mailto:${email.to ?? ''}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`;
  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium truncate">To: {email.vendor}{email.to ? ` <${email.to}>` : ''}</div>
          <div className="text-sm text-gray-600">Subject: {email.subject}</div>
        </div>
        <div className="flex shrink-0 gap-2">
          <StandardButton type="button" customVariant="white" onClick={copy}><Copy className="h-4 w-4 mr-2" />Copy</StandardButton>
          <StandardButton type="button" customVariant="white" onClick={() => { window.location.href = mailto; }}><Mail className="h-4 w-4 mr-2" />Open in email</StandardButton>
        </div>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{email.body}</p>
    </div>
  );
}

const VENDOR_COLUMN_WIDTH = 13; // rem
const ITEM_COLUMN_WIDTH = 16; // rem

/** The comparison the AI produced, already validated by the server: never the raw model output. */
export function BidComparisonResults({ result }: { result: AiBidComparison }) {
  const columns = useMemo<GenericTableColumn<AiLineItem>[]>(() => [
    {
      key: 'item',
      header: 'Item',
      width: `${ITEM_COLUMN_WIDTH}rem`,
      render: (row) => (
        <div className="py-1 whitespace-normal break-words">
          <div className="font-medium">{row.item}</div>
          {row.category && <div className="text-xs text-gray-500">{row.category}</div>}
        </div>
      ),
    },
    ...result.vendors.map((vendor, index) => ({
      key: `vendor-${index}`,
      header: vendor.name,
      width: `${VENDOR_COLUMN_WIDTH}rem`,
      render: (row: AiLineItem) => <OfferCell offer={row.offers[index]} />,
    })),
  ], [result.vendors]);

  const rec = result.recommendation;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comparison Results</CardTitle>
        <p className="text-sm text-gray-600">
          AI normalized the line items and compared {result.vendors.length} bids. Review the figures against the original documents before deciding.
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        {result.dataGaps.length > 0 && (
          <div className="rounded-lg border border-border bg-gray-50 p-4">
            <h3 className="flex items-center gap-2 text-base font-semibold mb-2"><AlertTriangle className="h-4 w-4" />Missing information</h3>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              {result.dataGaps.map((gap, index) => (
                <li key={index}>{gap.vendor ? <><span className="font-medium">{gap.vendor}:</span> {gap.issue}</> : gap.issue}</li>
              ))}
            </ul>
          </div>
        )}

        {result.summary && (
          <section>
            <h3 className="text-base font-semibold mb-2">Summary</h3>
            <p className="text-gray-700">{result.summary}</p>
          </section>
        )}

        <section>
          <h3 className="text-base font-semibold mb-2">Totals</h3>
          <VendorTotals vendors={result.vendors} />
        </section>

        <section>
          <h3 className="text-base font-semibold mb-2">Line items</h3>
          {result.items.length === 0 ? (
            <p className="text-sm text-gray-500">No line items could be extracted from these documents.</p>
          ) : (
            <GenericTable
              columns={columns}
              data={result.items}
              selectable={false}
              pageSize={Math.max(result.items.length, 1)}
              rowKey={(row, index) => `${index}-${row.item}`}
              minTableWidth={(ITEM_COLUMN_WIDTH + VENDOR_COLUMN_WIDTH * result.vendors.length) * 16}
            />
          )}
        </section>

        {result.missingItems.length > 0 && (
          <section>
            <h3 className="text-base font-semibold mb-2">Missing items by vendor</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.missingItems.map((entry) => (
                <div key={entry.vendor} className="rounded-lg border border-border p-3">
                  <div className="font-medium mb-1">{entry.vendor}</div>
                  <ul className="list-disc list-inside text-sm text-gray-700">
                    {entry.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h3 className="text-base font-semibold mb-2">Recommendation</h3>
          <div className="rounded-lg border border-border p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              {rec.vendor ? (
                <>
                  <span className="text-sm text-gray-600">Recommended vendor</span>
                  <span className="text-lg font-semibold">{rec.vendor}</span>
                </>
              ) : (
                <span className="text-lg font-semibold">No vendor can be recommended yet</span>
              )}
              <GenericTag type="status" value={CONFIDENCE_TAG[rec.confidence]}>{CONFIDENCE_LABEL[rec.confidence]}</GenericTag>
            </div>
            {rec.reasoning && <p className="text-gray-700">{rec.reasoning}</p>}
            {rec.caveats.length > 0 && (
              <ul className="list-disc list-inside text-sm text-gray-600">
                {rec.caveats.map((caveat) => <li key={caveat}>{caveat}</li>)}
              </ul>
            )}
          </div>
        </section>

        {result.clarificationEmails.length > 0 && (
          <section>
            <h3 className="text-base font-semibold mb-2">Clarification emails</h3>
            <div className="space-y-3">
              {result.clarificationEmails.map((email) => <ClarificationEmail key={email.vendor} email={email} />)}
            </div>
          </section>
        )}
      </CardContent>
    </Card>
  );
}
